<template>
  <Row>
    <i-col span="24">
      <AffixHeader>
        <BreadcrumbItem slot="parent" to="/contacts">Contacts</BreadcrumbItem>
        <span slot="current">{{ isNew ? "New" : contact.gsn }}</span>
        <span slot="title">{{ isNew ? "New" : "Edit" }} Contact</span>
        <span slot="sub-title">{{ fullname }}</span>
      </AffixHeader>

      <Tabs :animated="false">
        <TabPane label="Contact">
          <i-form ref="form" :model="contact" :rules="rules" label-position="top">
            <!-- Name -->
            <Row>
              <i-col span="4">
                <i-form-item label="Title" prop="title" style="padding-right: 6px;">
                  <i-select v-model="contact.title">
                    <i-option value="Mr">Mr</i-option>
                    <i-option value="Mrs">Mrs</i-option>
                    <i-option value="Ms">Ms</i-option>
                  </i-select>
                </i-form-item>
              </i-col>
              <i-col span="10">
                <i-form-item label="Name" prop="name" style="padding-right: 6px;">
                  <i-input v-model="contact.name"></i-input>
                </i-form-item>
              </i-col>
              <i-col span="10">
                <i-form-item label="Surname" prop="surname">
                  <i-input v-model="contact.surname"></i-input>
                </i-form-item>
              </i-col>
            </Row>

            <i-form-item label="Gender">
              <RadioGroup v-model="contact.gender">
                <Radio label="Male">
                  <FFIcon type="male"/>
                  <span>Male</span>
                </Radio>
                <Radio label="Female">
                  <FFIcon type="female"/>
                  <span>Female</span>
                </Radio>
              </RadioGroup>
            </i-form-item>

            <Row>
              <i-col span="12">
                <i-form-item label="Organisation" prop="organisation" style="padding-right: 6px;">
                  <i-input v-model="contact.organisation"></i-input>
                </i-form-item>
              </i-col>

              <i-col span="12">
                <i-form-item label="Job Title" prop="job_title">
                  <i-input v-model="contact.job_title"></i-input>
                </i-form-item>
              </i-col>
            </Row>

            <i-form-item label="Email" prop="email">
              <i-input v-model="contact.email"></i-input>
            </i-form-item>

            <i-form-item label="Phone" prop="phone">
              <i-input v-model="contact.phone"></i-input>
            </i-form-item>

            <i-form-item label="Skype" prop="skype">
              <i-input v-model="contact.skype"></i-input>
            </i-form-item>

            <i-form-item label="Countries of Operation">
              <TagSelectCountry v-model="contact.countries_of_operation"></TagSelectCountry>
            </i-form-item>

            <i-form-item label="Comment" prop="comment">
              <i-input
                v-model="contact.comment"
                type="textarea"
                spellcheck
                :autosize="{ minRows: 6, maxRows: 36 }"
                :rows="14"
              ></i-input>
            </i-form-item>

            <!-- Action Buttons -->
            <i-form-item class="action-buttons" v-if="$can('manage', 'contacts')">
              <i-button type="primary" @click="save">
                <FFIcon type="save"/>
                {{ isNew ? "Create" : "Update" }}
              </i-button>
            </i-form-item>
          </i-form>
        </TabPane>

        <TabPane label="Communications" v-if="!isNew">
          <!--
            $router.push({name: 'NewCommunicationRoute', params: {contact_id: id}})
          -->
          <i-button
            type="primary"
            v-if="$can('manage', 'communications')"
            @click="$router.push(`/communications/new/${id}`)"
          >
            <FFIcon type="plus"/>New Communication
          </i-button>
          <br>
          <br>
          <CommunicationsTable :conditions="{ contact_id: `eq.${id}` }" v-if="id > 0" v-once></CommunicationsTable>
        </TabPane>
      </Tabs>
    </i-col>
    <LoadingSpinner :loading="loading"></LoadingSpinner>
  </Row>
</template>

<script>
import database from "@/database";
import CommunicationsTable from "@/communications/table.vue";
import TagSelectCountry from "@/components/tag_select_country_by_iso3";

export default {
  components: {
    CommunicationsTable,
    TagSelectCountry,
  },
  props: ["id"],

  data() {
    return {
      loading: false,
      contact: {
        id: this.id,
        title: "",
        name: "",
        surname: "",
        gender: "Male",
        organisation: "",
        job_title: "",
        email: "",
        phone: "",
        skype: "",
        countries_of_operation: [],
        comment: "",
      },
      rules: {
        title: {
          required: true,
          trigger: "blur",
          message: "Title is required",
        },
        name: {
          required: true,
          trigger: "blur",
          message: "Name is required",
        },
        surname: {
          required: true,
          trigger: "blur",
          message: "Surname is required",
        },
        organisation: {
          required: true,
          trigger: "blur",
          message: "Organisation is required",
        },
        job_title: {
          required: true,
          trigger: "blur",
          message: "Job title is required",
        },
        email: {
          type: "email",
          trigger: "blur",
        },
      },
    };
  },

  created() {
    if (!!this.id) {
      this.load();
    }
  },

  computed: {
    isNew() {
      return !this.id;
    },
    fullname() {
      return (
        this.contact.title +
        " " +
        this.contact.name +
        " " +
        this.contact.surname
      );
    },
  },

  methods: {
    load() {
      if (!!this.id) {
        this.loading = true;
        database
          .load("contacts", this.id)
          .then(res => {
            this.contact = res.data;
            this.loading = false;
          })
          .catch(err => {
            console.error(err);
            this.loading = false;
          });
      }
    },

    save() {
      if (!this.$can("manage", "contacts")) {
        this.$Message.error("User is not permitted");
        return;
      }
      const isNew = this.isNew;
      this.$refs["form"].validate(valid => {
        if (valid) {
          database
            .save("contacts", this.contact, {})
            .then(res => {
              console.log(res);
              this.contact = res.data;
              this.loadingSuccess(
                isNew ? "Contact has been created" : "Contact has been updated"
              );
              this.$router.push(`/contacts/${this.contact.id}`);
            })
            .catch(err => {
              console.error(err.response);
              this.loadingError(
                isNew ? "Failed to create contact" : "Failed to update contact"
              );
            });
        } else {
          this.$Message.error("Form errors!");
        }
      });
    },
  },
};
</script>

<style lang="css"></style>
